/**
 * Validation utilities for organization feature
 * Provides input validation for IDs, emails, and other fields before API calls
 */

import { VALIDATION, DANGEROUS_PATTERNS } from '../config/constants';

/**
 * UUID validation pattern (version 4)
 * Matches: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * where x is any hexadecimal digit and y is one of 8, 9, A, or B
 */
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Organization ID pattern - accepts both UUIDs and alphanumeric slugs (e.g., "cmdqsit")
 */
export const ORG_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

/**
 * Short hex ID pattern - accepts the backend's real short hex division IDs (e.g. "F47E9422"),
 * distinct from ORG_ID_PATTERN since division IDs still need to reject non-hex garbage like "bad-id".
 * Also accepts the backend's collision-fallback format (generateDivisionId() appends "-XXXX" hex
 * when the 8-char id already exists, e.g. "F47E9422-1A2B") — otherwise validateDivisionId() throws
 * before the request ever fires for any division unlucky enough to collide.
 */
export const SHORT_HEX_ID_PATTERN = /^[0-9a-f]{6,32}(-[0-9a-f]{1,8})?$/i;

/**
 * Legacy tenant-slug division ID pattern — on legacy tenants a User's default Division reuses
 * the tenant's own ID as its divisionId (BackfillTenantDefaultDivision's documented "ID-reuse
 * rule"), e.g. divisionId "CMDQDEV" for tenant "CMDQDEV". Deliberately alphanumeric-only (no
 * hyphen), unlike ORG_ID_PATTERN — a division ID still needs to reject garbage like "bad-id".
 */
export const LEGACY_TENANT_DIVISION_ID_PATTERN = /^[a-zA-Z0-9]{4,32}$/;

/**
 * Email validation pattern (RFC 5322 simplified)
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Check if a string is a valid UUID
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid UUID format
 */
export const isValidUUID = (id) => {
  if (typeof id !== 'string') return false;
  return UUID_PATTERN.test(id);
};

/**
 * Check if a string is a valid division ID: full UUID, the backend's short hex format, or a
 * legacy tenant-slug ID reused as a divisionId (see LEGACY_TENANT_DIVISION_ID_PATTERN).
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid division ID format
 */
export const isValidDivisionId = (id) => {
  if (typeof id !== 'string') return false;
  return UUID_PATTERN.test(id) || SHORT_HEX_ID_PATTERN.test(id) || LEGACY_TENANT_DIVISION_ID_PATTERN.test(id);
};

/**
 * Check if a string is a valid email
 * @param {string} email - The email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
  if (typeof email !== 'string') return false;
  return EMAIL_PATTERN.test(email);
};

/**
 * Validate tenant ID
 * @param {string} tenantId - Tenant ID to validate
 * @throws {Error} If tenantId is invalid
 * @returns {boolean} True if valid
 */
export const validateTenantId = (tenantId) => {
  if (!tenantId) {
    throw new Error('Tenant ID is required');
  }
  if (typeof tenantId !== 'string' || !ORG_ID_PATTERN.test(tenantId)) {
    throw new Error('Invalid tenant ID format');
  }
  return true;
};

/** @deprecated Use validateTenantId() instead — organizationId was this app's old (incorrect)
 * name for the Tenant ID. Kept only until every caller migrates. */
export const validateOrgId = validateTenantId;

/**
 * Validate user ID
 * @param {string} userId - User ID to validate
 * @throws {Error} If userId is invalid
 * @returns {boolean} True if valid
 */
export const validateUserId = (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  if (!isValidUUID(userId)) {
    throw new Error('Invalid user ID format');
  }
  return true;
};

/**
 * Validate division ID
 * @param {string} divisionId - Division ID to validate
 * @throws {Error} If divisionId is invalid
 * @returns {boolean} True if valid
 */
export const validateDivisionId = (divisionId) => {
  if (!divisionId) {
    throw new Error('Division ID is required');
  }
  if (!isValidDivisionId(divisionId)) {
    throw new Error('Invalid division ID format');
  }
  return true;
};

/**
 * Validate group/team ID
 * @param {string} groupId - Group ID to validate
 * @throws {Error} If groupId is invalid
 * @returns {boolean} True if valid
 */
export const validateGroupId = (groupId) => {
  if (!groupId) {
    throw new Error('Group ID is required');
  }
  if (!isValidUUID(groupId)) {
    throw new Error('Invalid group ID format');
  }
  return true;
};

/**
 * Validate role ID
 * @param {string} roleId - Role ID to validate
 * @throws {Error} If roleId is invalid
 * @returns {boolean} True if valid
 */
export const validateRoleId = (roleId) => {
  if (!roleId) {
    throw new Error('Role ID is required');
  }
  if (!isValidUUID(roleId)) {
    throw new Error('Invalid role ID format');
  }
  return true;
};

/**
 * Validate invitation ID
 * @param {string} invitationId - Invitation ID to validate
 * @throws {Error} If invitationId is invalid
 * @returns {boolean} True if valid
 */
const INVITATION_TOKEN_PATTERN = /^([A-Z0-9]+_)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const validateInvitationId = (invitationId) => {
  if (!invitationId) {
    throw new Error('Invitation ID is required');
  }
  if (!INVITATION_TOKEN_PATTERN.test(invitationId)) {
    throw new Error('Invalid invitation ID format');
  }
  return true;
};

/**
 * Validate notification ID
 * @param {string} notificationId - Notification ID to validate
 * @throws {Error} If notificationId is invalid
 * @returns {boolean} True if valid
 */
export const validateNotificationId = (notificationId) => {
  if (!notificationId) {
    throw new Error('Notification ID is required');
  }
  if (!isValidUUID(notificationId)) {
    throw new Error('Invalid notification ID format');
  }
  return true;
};

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @throws {Error} If email is invalid
 * @returns {boolean} True if valid
 */
export const validateEmail = (email) => {
  if (!email) {
    throw new Error('Email is required');
  }
  if (!isValidEmail(email)) {
    throw new Error('Invalid email format');
  }
  return true;
};

/**
 * Validate that a value is a non-empty string
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @throws {Error} If value is invalid
 * @returns {boolean} True if valid
 */
export const validateNonEmptyString = (value, fieldName = 'Value') => {
  if (value === null || value === undefined) {
    throw new Error(`${fieldName} is required and must be a string`);
  }
  if (typeof value !== 'string') {
    throw new Error(`${fieldName} is required and must be a string`);
  }
  if (value.trim().length === 0) {
    throw new Error(`${fieldName} cannot be empty`);
  }
  return true;
};

/**
 * Validate multiple tenant IDs
 * @param {Array<string>} tenantIds - Array of tenant IDs to validate
 * @throws {Error} If any tenantId is invalid
 * @returns {boolean} True if all valid
 */
export const validateTenantIds = (tenantIds) => {
  if (!Array.isArray(tenantIds)) {
    throw new Error('Tenant IDs must be an array');
  }
  if (tenantIds.length === 0) {
    throw new Error('At least one tenant ID is required');
  }
  tenantIds.forEach((tenantId, index) => {
    if (!isValidUUID(tenantId)) {
      throw new Error(`Invalid tenant ID at index ${index}`);
    }
  });
  return true;
};

/** @deprecated Use validateTenantIds() instead. Kept only until every caller migrates. */
export const validateOrgIds = validateTenantIds;

/**
 * Validate multiple user IDs
 * @param {Array<string>} userIds - Array of user IDs to validate
 * @throws {Error} If any userId is invalid
 * @returns {boolean} True if all valid
 */
export const validateUserIds = (userIds) => {
  if (!Array.isArray(userIds)) {
    throw new Error('User IDs must be an array');
  }
  if (userIds.length === 0) {
    throw new Error('At least one user ID is required');
  }
  userIds.forEach((userId, index) => {
    if (!isValidUUID(userId)) {
      throw new Error(`Invalid user ID at index ${index}`);
    }
  });
  return true;
};

// ===== REAL-TIME INPUT FILTERS =====

/**
 * Filter a person name input, stripping invalid characters in real-time.
 * Allows: letters (including accented), spaces, hyphens, apostrophes
 * @param {string} value - Raw input value
 * @returns {string} Filtered value with only valid characters
 */
export const filterPersonNameInput = (value) => {
  if (!value) return '';
  return value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '');
};

/**
 * Filter an entity name input (role, division, team), stripping invalid characters.
 * Allows: letters (including accented), spaces, hyphens, apostrophes
 * Matches backend security policy: INVALID_NAME_FORMAT
 * @param {string} value - Raw input value
 * @returns {string} Filtered value with only valid characters
 */
export const filterEntityNameInput = (value) => {
  if (!value) return '';
  return value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '');
};

/**
 * Filter an entity name input that also allows numbers (teams, divisions).
 * Allows: letters (including accented), numbers, spaces, hyphens, apostrophes
 * @param {string} value - Raw input value
 * @returns {string} Filtered value with only valid characters
 */
export const filterEntityNameInputWithNumbers = (value) => {
  if (!value) return '';
  return value.replace(/[^a-zA-ZÀ-ÿ0-9\s'-]/g, '');
};

// ===== FORM FIELD VALIDATORS =====

/**
 * Check if a string contains dangerous/malicious input (XSS, injection, etc.)
 * @param {string} value - The input to check
 * @returns {boolean} True if the input contains dangerous patterns
 */
export const containsDangerousInput = (value) => {
  if (!value) return false;
  if (DANGEROUS_PATTERNS.some(pattern => pattern.test(value))) return true;
  if (VALIDATION.BLOCKED_CHARS.test(value)) return true;
  return false;
};

/**
 * Validate an entity name (role, division, team)
 * Rules: required, 2-70 chars, starts with letter, letters/spaces/hyphens/apostrophes only
 * Matches backend security policy: INVALID_NAME_FORMAT
 * @param {string} value - The name to validate
 * @param {string} label - Display label for error messages (e.g. "Role name")
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateEntityName = (value, label = 'Name') => {
  const trimmed = value?.trim() || '';

  if (!trimmed) return { valid: false, error: `${label} is required` };
  if (trimmed.length < VALIDATION.ENTITY_NAME.minLength) {
    return { valid: false, error: `${label} must be at least ${VALIDATION.ENTITY_NAME.minLength} characters` };
  }
  if (trimmed.length > VALIDATION.ENTITY_NAME.maxLength) {
    return { valid: false, error: `${label} cannot exceed ${VALIDATION.ENTITY_NAME.maxLength} characters` };
  }
  if (/^\s+$/.test(value)) return { valid: false, error: `${label} cannot contain only spaces` };
  if (!/^[a-zA-ZÀ-ÿ]/.test(trimmed)) return { valid: false, error: `${label} must start with a letter` };
  if (containsDangerousInput(value)) {
    return { valid: false, error: 'Only letters, spaces, hyphens, and underscores are allowed' };
  }
  if (!VALIDATION.ENTITY_NAME_PATTERN.test(trimmed)) {
    return { valid: false, error: 'Only letters, spaces, hyphens, and underscores are allowed' };
  }

  return { valid: true, error: null };
};

/**
 * Validate an entity name that also allows numbers (teams, divisions).
 * Rules: required, 2-30 chars, starts with letter, letters/numbers/spaces/hyphens/apostrophes
 * @param {string} value - The name to validate
 * @param {string} label - Display label for error messages
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateEntityNameWithNumbers = (value, label = 'Name') => {
  const trimmed = value?.trim() || '';

  if (!trimmed) return { valid: false, error: `${label} is required` };
  if (trimmed.length < VALIDATION.ENTITY_NAME.minLength) {
    return { valid: false, error: `${label} must be at least ${VALIDATION.ENTITY_NAME.minLength} characters` };
  }
  if (trimmed.length > VALIDATION.ENTITY_NAME.maxLength) {
    return { valid: false, error: `${label} cannot exceed ${VALIDATION.ENTITY_NAME.maxLength} characters` };
  }
  if (/^\s+$/.test(value)) return { valid: false, error: `${label} cannot contain only spaces` };
  if (!/^[a-zA-ZÀ-ÿ]/.test(trimmed)) return { valid: false, error: `${label} must start with a letter` };
  if (containsDangerousInput(value)) {
    return { valid: false, error: 'Only letters, numbers, spaces, hyphens, and underscores are allowed' };
  }
  if (!/^[a-zA-ZÀ-ÿ0-9\s\-_]+$/.test(trimmed)) {
    return { valid: false, error: 'Only letters, numbers, spaces, hyphens, and underscores are allowed' };
  }

  return { valid: true, error: null };
};

/**
 * Validate a person name (first name, last name)
 * Rules: required, 1-50 chars, letters/spaces/hyphens/apostrophes only (no numbers)
 * @param {string} value - The name to validate
 * @param {string} label - Display label for error messages (e.g. "First name")
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validatePersonName = (value, label = 'Name') => {
  const trimmed = value?.trim() || '';

  if (!trimmed) return { valid: false, error: `${label} is required` };
  if (trimmed.length < VALIDATION.PERSON_NAME.minLength) {
    return { valid: false, error: `${label} must be at least ${VALIDATION.PERSON_NAME.minLength} character` };
  }
  if (trimmed.length > VALIDATION.PERSON_NAME.maxLength) {
    return { valid: false, error: `${label} cannot exceed ${VALIDATION.PERSON_NAME.maxLength} characters` };
  }
  if (containsDangerousInput(value)) {
    return { valid: false, error: 'Only letters, spaces, hyphens, and apostrophes are allowed' };
  }
  if (!VALIDATION.PERSON_NAME_PATTERN.test(trimmed)) {
    return { valid: false, error: `${label} can only contain letters, spaces, hyphens, and apostrophes` };
  }

  return { valid: true, error: null };
};

/**
 * Validate a description field (optional)
 * Rules: optional, max 500 chars, no dangerous input
 * @param {string} value - The description to validate
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateDescription = (value) => {
  if (!value || !value.trim()) return { valid: true, error: null };

  if (value.trim().length > VALIDATION.DESCRIPTION.maxLength) {
    return { valid: false, error: `Description cannot exceed ${VALIDATION.DESCRIPTION.maxLength} characters` };
  }
  if (containsDangerousInput(value)) {
    return { valid: false, error: 'Description contains invalid characters' };
  }
  if (!VALIDATION.DESCRIPTION_PATTERN.test(value)) {
    return { valid: false, error: "Description can only contain letters, numbers, spaces, and basic punctuation (. , ' ?)" };
  }

  return { valid: true, error: null };
};

/**
 * Validate an email field
 * Rules: required, max 254 chars, valid email format
 * @param {string} value - The email to validate
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateEmailField = (value) => {
  const trimmed = value?.trim() || '';

  if (!trimmed) return { valid: false, error: 'Email is required' };
  if (trimmed.length > VALIDATION.EMAIL.maxLength) {
    return { valid: false, error: `Email cannot exceed ${VALIDATION.EMAIL.maxLength} characters` };
  }
  if (!EMAIL_PATTERN.test(trimmed)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  return { valid: true, error: null };
};
